import type { MeasurementQualifierInput } from '@/features/patient-record/hooks/useMedicalHistoryMutations';
import type {
  MeasurementType,
  MeasurementViewModel,
} from '@/features/patient-record/types';

import {
  defaultUnitFor,
  fromMetric,
  MEASUREMENT_UNIT,
  roundTo,
  toMetric,
  valueForType,
  type MeasurementUnit,
  type UnitSystem,
} from './unitConversion';

export const EDITABLE_TYPES: readonly MeasurementType[] = [
  'WEIGHT',
  'HEIGHT',
  'HEAD_CIRCUMFERENCE',
  'ARM_SPAN',
];

/**
 * One row of the measurement form. `inches` is the second box of the
 * feet + inches pair and is empty for every other unit.
 */
export interface FieldDraft {
  readonly unit: MeasurementUnit;
  readonly value: string;
  readonly inches: string;
}

export interface MeasurementDraft {
  readonly date: string;
  readonly notes: string;
  readonly WEIGHT: FieldDraft;
  readonly HEIGHT: FieldDraft;
  readonly HEAD_CIRCUMFERENCE: FieldDraft;
  readonly ARM_SPAN: FieldDraft;
}

export function emptyField(
  type: MeasurementType,
  system: UnitSystem,
): FieldDraft {
  return { unit: defaultUnitFor(type, system), value: '', inches: '' };
}

function parsePart(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isNaN(parsed) || parsed < 0 ? null : parsed;
}

/** The metric value a row would persist, or `null` when it holds nothing usable. */
export function metricValueOf(field: FieldDraft): number | null {
  const primary = parsePart(field.value);
  if (field.unit === MEASUREMENT_UNIT.ftIn) {
    // Either box alone is a complete height — 5 ft, or 11 in for an infant.
    const inches = parsePart(field.inches);
    if (primary === null && inches === null) return null;
    const metric = toMetric(field.unit, primary ?? 0, inches ?? 0);
    return metric > 0 ? roundTo(metric, 2) : null;
  }
  if (primary === null || primary <= 0) return null;
  return roundTo(toMetric(field.unit, primary), 2);
}

/** A filled-in row showing `metric` (kg / cm) expressed in `unit`. */
export function fieldFromMetric(
  metric: number,
  unit: MeasurementUnit,
): FieldDraft {
  const parts = fromMetric(unit, metric);
  return {
    unit,
    value: String(parts.primary),
    inches: unit === MEASUREMENT_UNIT.ftIn ? String(parts.inches) : '',
  };
}

/**
 * Switch a row to another unit, carrying the value across rather than
 * re-reading the same number in the new unit — a toggle must never change
 * what the patient measures.
 */
export function convertField(
  field: FieldDraft,
  nextUnit: MeasurementUnit,
): FieldDraft {
  const metric = metricValueOf(field);
  if (metric === null) return { unit: nextUnit, value: '', inches: '' };
  return fieldFromMetric(metric, nextUnit);
}

/** A measurement needs a date and at least one positive value to persist. */
export function hasValidValue(draft: MeasurementDraft): boolean {
  return EDITABLE_TYPES.some((type) => metricValueOf(draft[type]) !== null);
}

export function toQualifiers(
  draft: MeasurementDraft,
): MeasurementQualifierInput[] {
  const qualifiers: MeasurementQualifierInput[] = [];
  for (const type of EDITABLE_TYPES) {
    const metric = metricValueOf(draft[type]);
    if (metric === null) continue;
    qualifiers.push(
      type === 'WEIGHT'
        ? { measurementType: 'WEIGHT', valueKg: metric }
        : { measurementType: type, valueCm: metric },
    );
  }
  return qualifiers;
}

export function toDraft(
  record: MeasurementViewModel | undefined,
  system: UnitSystem,
): MeasurementDraft {
  const fields: Record<MeasurementType, FieldDraft> = {
    WEIGHT: emptyField('WEIGHT', system),
    HEIGHT: emptyField('HEIGHT', system),
    HEAD_CIRCUMFERENCE: emptyField('HEAD_CIRCUMFERENCE', system),
    ARM_SPAN: emptyField('ARM_SPAN', system),
  };
  if (record === undefined) {
    return {
      date: new Date().toISOString().slice(0, 10),
      notes: '',
      ...fields,
    };
  }
  for (const type of EDITABLE_TYPES) {
    const qualifier = record.qualifiers.find((q) => q.measurementType === type);
    if (qualifier === undefined) continue;
    const metric = valueForType(qualifier, type);
    if (metric === null) continue;
    fields[type] = fieldFromMetric(metric, fields[type].unit);
  }
  return {
    date: record.dateMeasured,
    notes: record.notes ?? '',
    ...fields,
  };
}
